import { baseTheme } from './base';
import type { ThemeDefinition } from '../types';

export const lightTheme: ThemeDefinition = {
  ...baseTheme,
  colors: {
    ...baseTheme.colors,

    // Background
    bg: 'white',
    'bg.base': 'gray.50',
    'bg.sunken': 'gray.100',
    'bg.raised': 'gray.50',
    'bg.floating': 'white',
    'bg.inverted': 'black',
    'bg.panel': 'white',
    'bg.overlay': 'white',
    'bg.error': 'red.100',
    'bg.error.solid': 'red.600',
    'bg.warning': 'goldenGlow.100',
    'bg.warning.solid': 'goldenGlow.300',
    'bg.success': 'green.100',
    'bg.success.solid': 'green.400',
    'bg.info': 'blue.100',
    'bg.info.solid': 'blue.500',
    'bg.info.inverted': 'blue.500',
    'bg.accent': 'cyan.50',
    'bg.accent.solid': 'cyan.600',

    // Text
    fg: 'gray.950',
    'fg.constant': 'gray.950',
    'fg.disabled': 'gray.500',
    'fg.muted': 'gray.600',
    'fg.muted.constant': 'gray.600',
    'fg.inverted': 'gray.50',
    'fg.inverted.constant': 'gray.50',
    'fg.error': 'red.700',
    'fg.warning': 'goldenGlow.700',
    'fg.success': 'green.700',
    'fg.accent': 'cyan.700',

    // Border
    border: 'gray.200',
    'border.subtle': 'gray.100',
    'border.inverted': 'gray.800',
    'border.error': 'red.500',
    'border.warning': 'goldenGlow.500',
    'border.success': 'green.500',
    'border.info': 'blue.500',
    'border.accent': 'cyan.500',

    // Card
    'card.bg.emphasized': 'gray.200',
    'card.bg.emphasized.hover': 'gray.300',
    'card.emphasized.stripes': 'whiteAlpha.400',
    'card.bg.emphasized.infobox': 'whiteAlpha.600',
    'card.bg.muted': 'gray.100',
    'card.bg.muted.hover': 'gray.200',
    'card.bg.active.infobox': 'blackAlpha.600',
    'card.bg.delay': 'goldenGlow.200',
    'card.bg.delay.hover': 'goldenGlow.300',
    'card.active.stripes': 'whiteAlpha.400',
    'card.bg.ahead': 'titanWhite.200',
    'card.bg.ahead.hover': 'titanWhite.300',
    'card.bg.ontime': 'teal.200',
    'card.bg.ontime.hover': 'teal.300',
    'card.bg.subtle': 'gray.50',
    'card.bg.subtle.hover': 'gray.100',

    // Button
    'button.bg.primary': 'cyan.600',
    'button.bg.primary.hover': 'cyan.700',
    'button.bg.primary.active': 'cyan.800',
    'button.bg.primary.subtle': 'cyan.100',
    'button.bg.primary.subtle.hover': 'cyan.200',
    'button.bg.primary.subtle.active': 'cyan.300',
    'button.bg.secondary': 'gray.100',
    'button.bg.secondary.hover': 'gray.200',
    'button.bg.secondary.active': 'gray.300',
    'button.bg.ghost': 'transparent',
    'button.bg.ghost.hover': 'blackAlpha.100',
    'button.bg.ghost.active': 'blackAlpha.200',
    'button.bg.critical': 'red.600',
    'button.bg.critical.hover': 'red.700',
    'button.bg.critical.active': 'red.800',
    'button.bg.critical.subtle': 'red.100',
    'button.bg.critical.subtle.hover': 'red.200',
    'button.bg.critical.subtle.active': 'red.300',
    'button.bg.warning.subtle': 'goldenGlow.100',
    'button.bg.warning.subtle.hover': 'goldenGlow.200',
    'button.bg.warning.subtle.active': 'goldenGlow.300',
    'button.bg.ai': 'titanWhite.600',
    'button.bg.ai.hover': 'titanWhite.700',
    'button.bg.ai.active': 'titanWhite.800',

    // Tag
    'tag.secondary.bg.default': 'gray.100',
    'tag.secondary.bg.hover': 'gray.200',
    'tag.secondary.bg.active': 'gray.300',
    'tag.secondary.solid.bg.default': 'gray.300',
    'tag.secondary.solid.bg.hover': 'gray.400',
    'tag.secondary.solid.bg.active': 'gray.500',
    'tag.primary.bg.default': 'cyan.100',
    'tag.primary.bg.hover': 'cyan.200',
    'tag.primary.bg.active': 'cyan.300',
    'tag.primary.solid.bg.default': 'cyan.600',
    'tag.primary.solid.bg.hover': 'cyan.700',
    'tag.primary.solid.bg.active': 'cyan.800',
    'tag.warning.bg.default': 'goldenGlow.100',
    'tag.warning.bg.hover': 'goldenGlow.200',
    'tag.warning.bg.selected': 'goldenGlow.300',
    'tag.warning.solid.bg.default': 'goldenGlow.400',
    'tag.warning.solid.bg.hover': 'goldenGlow.500',
    'tag.warning.solid.bg.active': 'goldenGlow.600',
    'tag.critical.bg.default': 'red.100',
    'tag.critical.bg.hover': 'red.200',
    'tag.critical.bg.active': 'red.300',
    'tag.critical.solid.bg.default': 'red.600',
    'tag.critical.solid.bg.hover': 'red.700',
    'tag.critical.solid.bg.active': 'red.800',
    'tag.ai.bg.default': 'titanWhite.100',
    'tag.ai.bg.hover': 'titanWhite.200',
    'tag.ai.bg.active': 'titanWhite.300',
    'tag.ai.solid.bg.default': 'titanWhite.600',
    'tag.ai.solid.bg.hover': 'titanWhite.700',
    'tag.ai.solid.bg.active': 'titanWhite.800',

    // Scrollbar
    'scrollbar.track': 'transparent',
    'scrollbar.thumb': 'gray.300',

    // Input
    'input.bg': 'white',
    'input.bg.hover': 'gray.50',
    'input.bg.focus': 'white',
    'input.bg.disabled': 'gray.200',
    'input.border': 'gray.300',
    'input.border.hover': 'gray.400',
    'input.border.focus': 'cyan.500',
    'input.border.error': 'red.500',
    'input.border.success': 'green.500',
    'input.text': 'gray.950',
    'input.text.placeholder': 'gray.500',
    'input.text.disabled': 'gray.500',
  },
};
